import { useEffect, useMemo, useRef, useState } from "react";
import type { ChannelCatalogItem, DelegationView, InboxGroup } from "../../../api/connectionHub.types";
import { INBOX_ACTION } from "../../../api/connectionHub.types";
import { connectionHubApi } from "../../../api/connectionHub.api";
import { isDelegationExpired } from "../../../utils/delegationValidity";
import { isConnectionHubError } from "../../../utils/handleConnectionError";
import InboxCard from "../InboxCard/InboxCard";
import styles from "./DelegatedInboxCard.module.scss";

export interface DelegatedInboxCardProps {
  group: InboxGroup;
  channels?: ChannelCatalogItem[];
  agencyClientId: number | null;
}

const noop = (): void => undefined;

const DelegatedInboxCard = ({ group, channels = [], agencyClientId }: DelegatedInboxCardProps): JSX.Element => {
  const [delegation, setDelegation] = useState<DelegationView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const channelsByIpId = useMemo(() => new Map(channels.map((channel) => [channel.ipId, channel])), [channels]);
  const actionsKey = group.actions.join(",");
  const canResend = group.actions.includes(INBOX_ACTION.RESEND_LINK);
  const autoGeneratedRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    setError(null);
    void connectionHubApi.getDelegation(group.inbox.id, agencyClientId)
      .then(async (view) => {
        if (!active) return;
        setDelegation(view);
        const neverSent = !view.lastSentAt && (view.sendCount ?? 0) === 0;
        if (view.shareUrl || !canResend || !neverSent || isDelegationExpired(view)) return;
        if (autoGeneratedRef.current === group.inbox.id) return;
        autoGeneratedRef.current = group.inbox.id;
        setBusy(true);
        try {
          const generated = await connectionHubApi.resendDelegation(group.inbox.id, { agencyClientId });
          if (active) setDelegation(generated);
        } catch (caught) {
          if (active && isConnectionHubError(caught)) setError(caught.message);
        } finally {
          setBusy(false);
        }
      })
      .catch((caught) => { if (active && isConnectionHubError(caught)) setError(caught.message); });
    return () => { active = false; };
  }, [actionsKey, agencyClientId, canResend, group.inbox.id]);

  const resend = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      setDelegation(await connectionHubApi.resendDelegation(group.inbox.id, { agencyClientId }));
    } catch (caught) {
      if (isConnectionHubError(caught)) setError(caught.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.root}>
      <InboxCard
      group={group}
      channelsByIpId={channelsByIpId}
      delegation={delegation}
      delegationBusy={busy}
      delegationError={error}
      actionsDisabled={busy}
      onResendDelegation={() => { void resend(); }}
      onConfirmForwarding={noop}
      onConfirmFilter={noop}
      onVerify={noop}
      />
    </div>
  );
};

export default DelegatedInboxCard;
